import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCreditCard, FiBarChart2, FiClipboard, FiFileText, FiDollarSign, FiCheckCircle, FiClock, FiTrendingUp, FiUser, FiArrowLeft, FiSend, FiEdit2, FiX, FiEye } from 'react-icons/fi';
import { useToast } from '../components/Toast';

const API_URL = `${process.env.REACT_APP_API_URL}/api/payments`;

const inputClass = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors disabled:bg-gray-50 disabled:text-gray-400';
const selectClass = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-white';

function PaymentManagement() {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [commissionRate, setCommissionRate] = useState(15);
  const [adminNotes, setAdminNotes] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentNotes, setPaymentNotes] = useState('');

  useEffect(() => {
    fetchSummary();
    fetchAllPayments();
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}?summary=true`);
      if (response.data.success) {
        setSummary(response.data.summary);
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  const fetchAllPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?limit=1000`);
      if (response.data.success) {
        setPayments(response.data.payments);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleLawyerSelect = async (lawyer) => {
    setSelectedLawyer(lawyer);
    setActiveTab('payments');
  };

  const handlePaymentSelect = (payment) => {
    setSelectedPayment(payment);
    setCommissionRate(payment.commission_rate || 15);
    setAdminNotes(payment.admin_notes || '');
    setPaymentStatus(payment.payment_status);
  };

  const handleUpdatePayment = async () => {
    try {
      const response = await axios.put(`${API_URL}`, {
        id: selectedPayment.id,
        commission_rate: parseFloat(commissionRate),
        admin_notes: adminNotes,
        payment_status: paymentStatus
      });

      if (response.data.success) {
        toast.success('Payment updated successfully!');
        setSelectedPayment(null);
        fetchAllPayments();
        fetchSummary();
      }
    } catch (err) {
      console.error('Error updating payment:', err);
      toast.error('Failed to update payment');
    }
  };

  const handleRecordCommissionPayment = async () => {
    if (!paymentAmount || !selectedLawyer) {
      toast.warning('Please enter payment amount');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('action', 'pay_commission');
      formData.append('lawyer_id', selectedLawyer.lawyer_id);
      formData.append('amount', paymentAmount);
      formData.append('payment_method', paymentMethod);
      formData.append('notes', paymentNotes);

      const response = await axios.post(`${API_URL}`, formData);

      if (response.data.success) {
        toast.success('Commission payment recorded successfully!');
        setShowPaymentModal(false);
        setPaymentAmount('');
        setPaymentNotes('');
        fetchSummary();
      }
    } catch (err) {
      console.error('Error recording payment:', err);
      toast.error('Failed to record payment');
    }
  };

  const lawyerPayments = selectedLawyer
    ? payments.filter(p => p.lawyer_id === selectedLawyer.lawyer_id)
    : [];

  const totalRevenue = (summary && Array.isArray(summary)) ? summary.reduce((sum, l) => sum + (l.total_revenue || 0), 0) : 0;
  const totalCommission = (summary && Array.isArray(summary)) ? summary.reduce((sum, l) => sum + (l.platform_commission || 0), 0) : 0;
  const totalLawyerEarnings = (summary && Array.isArray(summary)) ? summary.reduce((sum, l) => sum + (l.lawyer_earnings || 0), 0) : 0;
  const totalPaid = (summary && Array.isArray(summary)) ? summary.reduce((sum, l) => sum + (l.paid_revenue || 0), 0) : 0;
  const totalPending = (summary && Array.isArray(summary)) ? summary.reduce((sum, l) => sum + (l.pending_revenue || 0), 0) : 0;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiBarChart2 },
    { id: 'payments', label: 'Lawyer Payments', icon: FiClipboard },
    { id: 'ledger', label: 'Commission Ledger', icon: FiFileText },
  ];

  const statusBadge = (status) => {
    const map = {
      paid: 'bg-green-50 text-green-700',
      pending: 'bg-amber-50 text-amber-700',
      failed: 'bg-red-50 text-red-700',
    };
    return map[status] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary via-primary-light to-[#2a4a6f] text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1 initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <FiCreditCard className="w-7 h-7" /> Payment Management
          </motion.h1>
          <p className="mt-1 text-white/70 text-sm">Track and manage lawyer payments and commission</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
                }`}
              >
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                { label: 'Total Revenue', value: totalRevenue, icon: FiDollarSign, color: 'text-primary', bg: 'bg-primary/5' },
                { label: 'Paid Amount', value: totalPaid, icon: FiCheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
                { label: 'Pending Amount', value: totalPending, icon: FiClock, color: 'text-amber-600', bg: 'bg-amber-50' },
                { label: 'Platform Commission', value: totalCommission, icon: FiTrendingUp, color: 'text-accent', bg: 'bg-accent/10' },
                { label: 'Lawyer Earnings', value: totalLawyerEarnings, icon: FiUser, color: 'text-blue-600', bg: 'bg-blue-50' },
              ].map((card, idx) => {
                const Icon = card.icon;
                return (
                  <motion.div key={card.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} className="bg-white rounded-2xl shadow-card p-5">
                    <div className={`w-10 h-10 ${card.bg} rounded-xl flex items-center justify-center mb-3`}>
                      <Icon className={`w-5 h-5 ${card.color}`} />
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{card.label}</p>
                    <p className="text-lg font-bold text-gray-900">₹{card.value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                  </motion.div>
                );
              })}
            </div>

            {/* Lawyers Summary Table */}
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <div className="p-5 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FiUser className="w-5 h-5 text-primary" /> Lawyer Payment Summary</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 font-semibold text-gray-600">Lawyer</th>
                      <th className="px-4 py-3 font-semibold text-gray-600">Specialization</th>
                      <th className="px-4 py-3 font-semibold text-gray-600 text-center">Bookings</th>
                      <th className="px-4 py-3 font-semibold text-gray-600 text-right">Revenue</th>
                      <th className="px-4 py-3 font-semibold text-gray-600 text-center">Rate</th>
                      <th className="px-4 py-3 font-semibold text-gray-600 text-right">Commission</th>
                      <th className="px-4 py-3 font-semibold text-gray-600 text-right">Earnings</th>
                      <th className="px-4 py-3 font-semibold text-gray-600 text-right">Paid</th>
                      <th className="px-4 py-3 font-semibold text-gray-600 text-right">Pending</th>
                      <th className="px-4 py-3 font-semibold text-gray-600 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {summary && Array.isArray(summary) && summary.map(lawyer => (
                      <tr key={lawyer.lawyer_id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {lawyer.profile_image ? (
                              <img src={`${process.env.REACT_APP_API_URL}${lawyer.profile_image}`} alt={lawyer.lawyer_name} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <FiUser className="w-4 h-4 text-primary" />
                              </div>
                            )}
                            <span className="font-medium text-gray-900">{lawyer.lawyer_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{lawyer.specialization}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2.5 py-0.5 bg-primary/5 text-primary text-xs font-bold rounded-full">{lawyer.total_bookings}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">₹{(lawyer.total_revenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{lawyer.commission_rate}%</td>
                        <td className="px-4 py-3 text-right text-gray-600">₹{(lawyer.platform_commission || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-right text-gray-600">₹{(lawyer.lawyer_earnings || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-right text-green-600 font-medium">₹{(lawyer.paid_revenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-right text-amber-600 font-medium">₹{(lawyer.pending_revenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => handleLawyerSelect(lawyer)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors">
                            <FiEye className="w-3.5 h-3.5" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {selectedLawyer ? (
              <div className="space-y-6">
                {/* Lawyer Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white rounded-2xl shadow-card p-5">
                  <div className="flex items-center gap-4">
                    <button onClick={() => setSelectedLawyer(null)} className="inline-flex items-center gap-1.5 text-sm text-primary hover:bg-primary/5 px-3 py-1.5 rounded-lg transition-colors">
                      <FiArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">{selectedLawyer.lawyer_name}</h2>
                      <p className="text-sm text-gray-500">{selectedLawyer.specialization}</p>
                    </div>
                  </div>
                  <button onClick={() => setShowPaymentModal(true)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent text-white text-sm font-semibold rounded-xl hover:bg-accent/90 shadow-sm transition-colors">
                    <FiSend className="w-4 h-4" /> Pay Commission
                  </button>
                </div>

                {/* Mini Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    { label: 'Total Revenue', value: `₹${(selectedLawyer.total_revenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` },
                    { label: 'Lawyer Earnings', value: `₹${(selectedLawyer.lawyer_earnings || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` },
                    { label: 'Commission Rate', value: `${selectedLawyer.commission_rate || 15}%` },
                    { label: 'Paid', value: `₹${(selectedLawyer.total_commission_paid || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` },
                    { label: 'Pending', value: `₹${(selectedLawyer.pending_commission || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` },
                  ].map(stat => (
                    <div key={stat.label} className="bg-white rounded-xl shadow-sm p-4 text-center">
                      <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                      <p className="text-sm font-bold text-gray-900">{stat.value}</p>
                    </div>
                  ))}
                </div>

                {/* Booking Payments Table */}
                <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                  <div className="p-5 border-b border-gray-100">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2"><FiClipboard className="w-5 h-5 text-primary" /> Booking Payments</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left">
                          <th className="px-4 py-3 font-semibold text-gray-600">Invoice</th>
                          <th className="px-4 py-3 font-semibold text-gray-600">Client</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-right">Consult. Fee</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-right">Platform</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-right">GST</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-right">Total</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-right">Commission</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-center">Status</th>
                          <th className="px-4 py-3 font-semibold text-gray-600">Date</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {lawyerPayments && lawyerPayments.map(payment => (
                          <tr key={payment.id} className={`hover:bg-gray-50/50 transition-colors ${selectedPayment?.id === payment.id ? 'bg-primary/5' : ''}`}>
                            <td className="px-4 py-3 font-medium text-gray-900">{payment.invoice_number || 'N/A'}</td>
                            <td className="px-4 py-3 text-gray-600">{payment.client_name || 'N/A'}</td>
                            <td className="px-4 py-3 text-right text-gray-600">₹{(payment.consultation_fee || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-right text-gray-600">₹{(payment.platform_fee || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-right text-gray-600">₹{(payment.gst || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">₹{(payment.total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-right text-gray-600">₹{(payment.platform_commission || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex px-2.5 py-0.5 text-xs font-bold rounded-full ${statusBadge(payment.payment_status)}`}>
                                {payment.payment_status.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-500">{new Date(payment.created_at).toLocaleDateString()}</td>
                            <td className="px-4 py-3 text-center">
                              <button onClick={() => handlePaymentSelect(payment)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors">
                                <FiEdit2 className="w-3.5 h-3.5" /> Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-card p-16 text-center">
                <FiClipboard className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">Select a lawyer from the Overview tab to view their payments</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Ledger Tab */}
        {activeTab === 'ledger' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="bg-white rounded-2xl shadow-card p-8">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2"><FiFileText className="w-5 h-5 text-primary" /> Commission Payment Ledger</h2>
              <p className="text-sm text-gray-500 mb-8">Track all commission payments made to lawyers</p>
              <div className="bg-gray-50 rounded-2xl p-12 text-center">
                <FiFileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-400">Commission ledger feature coming soon</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Payment Detail Modal */}
      <AnimatePresence>
        {selectedPayment && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedPayment(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">Edit Payment Details</h2>
                <button onClick={() => setSelectedPayment(null)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"><FiX className="w-4 h-4 text-gray-400" /></button>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Invoice Number</label>
                  <input type="text" value={selectedPayment.invoice_number} disabled className={inputClass} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Status</label>
                  <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className={selectClass}>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Commission Rate (%)</label>
                  <input type="number" value={commissionRate} onChange={(e) => setCommissionRate(e.target.value)} className={inputClass} min="0" max="100" step="0.01" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Admin Notes</label>
                  <textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} className={`${inputClass} resize-none`} rows="3" placeholder="Add any notes..." />
                </div>

                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2 text-sm mb-3"><FiDollarSign className="w-4 h-4 text-primary" /> Payment Breakdown</h3>
                  <div className="flex justify-between text-sm text-gray-600"><span>Consultation Fee:</span><span>₹{(selectedPayment.consultation_fee || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between text-sm text-gray-600"><span>Platform Fee:</span><span>₹{(selectedPayment.platform_fee || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between text-sm text-gray-600"><span>GST:</span><span>₹{(selectedPayment.gst || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between text-sm font-bold text-gray-900 pt-2 border-t border-gray-200"><span>Total Amount:</span><span>₹{(selectedPayment.total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
                <button onClick={() => setSelectedPayment(null)} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
                <button onClick={handleUpdatePayment} className="px-5 py-2.5 text-sm font-semibold text-white bg-primary rounded-xl hover:bg-primary-light shadow-sm transition-colors">Save Changes</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Commission Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedLawyer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPaymentModal(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FiSend className="w-5 h-5 text-accent" /> Record Commission Payment</h2>
                <button onClick={() => setShowPaymentModal(false)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"><FiX className="w-4 h-4 text-gray-400" /></button>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Lawyer</label>
                  <input type="text" value={selectedLawyer.lawyer_name} disabled className={inputClass} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Pending Commission</label>
                  <div className="text-xl font-bold text-amber-600">₹{(selectedLawyer.pending_commission || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Amount (₹) *</label>
                  <input type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} className={inputClass} placeholder="Enter amount" min="0" step="0.01" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Method</label>
                  <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={selectClass}>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="upi">UPI</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
                  <textarea value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} className={`${inputClass} resize-none`} rows="3" placeholder="Add payment notes..." />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
                <button onClick={() => setShowPaymentModal(false)} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
                <button onClick={handleRecordCommissionPayment} className="px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 shadow-sm transition-colors">Record Payment</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default PaymentManagement;
